/**
 * Text Field Component
 *
 * A reusable form text input component that integrates with react-hook-form.
 * This component supports:
 * - Form validation with error messages
 * - Display toggling via a switch
 * - Customizable styling and layout
 * - Disabled state handling
 * - Integration with form groups
 *
 * This component is used throughout the application for text inputs,
 * providing consistent styling and behavior.
 *
 * @module components/reusable-form-fields/text-field
 */

import styled from "styled-components";
import { useFormContext } from "react-hook-form";
import { Form, Tooltip, FormGroup, OverlayTrigger } from "react-bootstrap";
import ValidationMessage from "../validation-message/validation-message";

/**
 * Styled form group that adjusts layout when used in input groups
 *
 * When within a form input group, it displays as a flex container
 * to allow proper alignment with adjacent elements
 */
const FCFormGroup = styled(FormGroup)`
  ${(props) => {
    return (
      props.$isingroup === "true" &&
      `display: flex;
      flex : 1`
    );
  }}
`;

/**
 * Styled text input that adjusts styling when used in input groups
 *
 * When within a form input group, it removes the right border radius
 * to visually connect with adjacent elements
 */
const FCTextField = styled(Form.Control)`
  ${(props) => {
    return (
      props.$isingroup === "true" &&
      `border-top-right-radius: 0;
      border-bottom-right-radius: 0;`
    );
  }}
`;

/**
 * Text field component for form inputs
 *
 * @param {Object} props - Component properties
 * @param {string} props.name - Field name for form registration
 * @param {string} [props.label] - Field label text
 * @param {string} [props.placeholder] - Placeholder text
 * @param {string} [props.type="text"] - Input type (text, email, password, etc.)
 * @param {Function} [props.register] - React Hook Form register function (optional if using FormContext)
 * @param {Object} [props.errors] - Validation errors for this field
 * @param {boolean} [props.boldLabel=false] - Whether to display label in bold
 * @param {boolean} [props.disabled=false] - Whether the field is disabled
 * @param {boolean} [props.hideErrMsg=false] - Whether to hide error messages
 * @param {string} [props.width] - Custom width for the input
 * @param {boolean} [props.isInGroup] - Whether the field is part of an input group
 * @param {boolean} [props.setDisplay] - Whether to show display toggle switch
 * @param {boolean} [props.displayFieldWatcher] - State of display toggle
 * @param {string} [props.fieldWatcherName] - Name for the display toggle field
 * @param {string} [props.defaultValue] - Default field value
 * @param {string} [props.messageHeight] - Height for error message
 * @param {boolean} [props.blurOnScroll] - Whether to blur input on scroll (useful for numeric inputs)
 * @param {string} [props.inputSize] - Size of the input (sm, md, lg)
 * @param {string} [props.textFieldClasses] - Additional CSS classes for the input
 * @param {boolean} [props.noSpacing] - Whether to remove margin bottom
 * @returns {JSX.Element} Text field component
 */
const TextField = (props) => {
  // Get register function from FormContext if not provided directly
  const { register } = useFormContext();

  return (
    <FCFormGroup
      className={!props.noSpacing ? "mb-3" : ""}
      controlId={`${props.label} ${props.name}`}
      // isingroup={props.isInGroup ? "true" : "false"}
    >
      <div className="d-flex">
        {props.label && (
          <Form.Label
            className={`${props.boldLabel ? "fw-bold" : "fw-normal"} ${
              props.errors ? "text-danger" : ""
            }`}
          >
            {props.label}
            {props.errors && "*"}
          </Form.Label>
        )}
        {props.setDisplay && (
          <div className="ms-2">
            <OverlayTrigger
              overlay={
                <Tooltip>
                  {props.displayFieldWatcher ? "Set Color" : "Transparent"}
                </Tooltip>
              }
            >
              <Form.Check
                id="display-switch"
                role="button"
                type="switch"
                {...register(props.fieldWatcherName)}
              />
            </OverlayTrigger>
          </div>
        )}
      </div>
      <FCTextField
        {...(props.register && { ...props.register(props.name) })}
        $isingroup={props.isInGroup ? "true" : "false"}
        style={{
          width: props.width ?? "100%",
          opacity:
            props.setDisplay && Boolean(!props.displayFieldWatcher) ? 0.3 : 1
        }}
        as={props.multiline ? "textarea" : props.as}
        rows={props.multiline ? 2 : props.rows}
        type={props.multiline ? undefined : props.type}
        placeholder={props.placeholder}
        className={`form-control ${props.textFieldClasses} ${
          props.errors ? "border-danger" : ""
        }`}
        disabled={
          props.disabled ||
          (props.setDisplay && Boolean(!props.displayFieldWatcher))
        }
        onWheel={({ target }) => {
          if (props.blurOnScroll) {
            target.blur();
          }
        }}
        onKeyDown={(e) => {
          if (props.handleKeyDown) {
            props.handleKeyDown(e);
          }
        }}
        size={props.inputSize || "md"}
        defaultValue={props.defaultValue}
      />
      {props.errors && !props.hideErrMsg && (
        <ValidationMessage
          error={props.errors?.message}
          $height={props.messageHeight}
        />
      )}
    </FCFormGroup>
  );
};

export default TextField;
